import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Sparkles, 
  Clock, 
  CheckCircle2,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

export function FeaturesPage() {
  const navigate = useNavigate();

  const upcomingFeatures = [
    {
      title: 'Advanced Analytics Dashboard',
      description: 'Comprehensive analytics with real-time data visualization and custom reports',
      status: 'In Development',
      progress: 65,
    },
    {
      title: 'User Role Management',
      description: 'Granular permission system with custom roles and access controls',
      status: 'Planned',
      progress: 0,
    },
    {
      title: 'API Management',
      description: 'Manage API keys, rate limits, and monitor API usage across the platform',
      status: 'Planned',
      progress: 0,
    },
    {
      title: 'Notification Center',
      description: 'Centralized notification system with email, SMS, and in-app notifications',
      status: 'In Development',
      progress: 40,
    },
    {
      title: 'Audit Logs',
      description: 'Complete audit trail of all system activities and user actions',
      status: 'Planned',
      progress: 0,
    },
    {
      title: 'Custom Integrations',
      description: 'Connect with third-party services and build custom integrations',
      status: 'Planned',
      progress: 0,
    },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-stroke-primary bg-background">
        <div className="p-6">
          <div className="flex items-center space-x-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="mb-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-500">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">Features</h1>
              <p className="text-text-secondary">Coming soon - Platform features and capabilities</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-8">
        <div className="max-w-4xl mx-auto">
          {/* Coming Soon Banner */}
          <Card className="mb-8 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500 flex-shrink-0">
                  <Clock className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    Features Coming Soon
                  </h3>
                  <p className="text-text-secondary">
                    We're working hard to bring you powerful new features to manage and enhance your platform. 
                    Check back soon for updates!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Features List */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              Upcoming Features
            </h2>
          </div>

          <div className="space-y-4">
            {upcomingFeatures.map((feature, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{feature.title}</CardTitle>
                      <CardDescription>{feature.description}</CardDescription>
                    </div>
                    <Badge 
                      variant={feature.status === 'In Development' ? 'success' : 'outline'}
                      className="ml-4"
                    >
                      {feature.status}
                    </Badge>
                  </div>
                </CardHeader>
                {feature.progress > 0 && (
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-text-secondary">Progress</span>
                        <span className="text-text-primary font-medium">{feature.progress}%</span>
                      </div>
                      <div className="w-full bg-surface-secondary rounded-full h-2">
                        <div
                          className="bg-button-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${feature.progress}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>

          {/* Call to Action */}
          <Card className="mt-8 border-stroke-primary">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Stay Updated
              </h3>
              <p className="text-text-secondary mb-4">
                We'll notify you as soon as these features become available. 
                In the meantime, explore our current features!
              </p>
              <Button onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
